import { useContext, useEffect, useState, useCallback } from "react";
import { ThemeContext } from "../../components/ThemeContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";


interface WordPressPost {
    id: number;
    title: {
        rendered: string;
    };
    content: {
        rendered: string;
    };
    acf?: {
        kratky_popis?: string;
    };
    link: string;
    translations: {
        sk?: number;
        en?: number;
    };
    _embedded?: {
        'wp:featuredmedia'?: Array<{
            source_url: string;
        }>;
    };
}

export const BlogDetail = () => {
    const [theme] = useContext(ThemeContext);
    const [post, setPost] = useState<WordPressPost | null>(null);
    const [loading, setLoading] = useState(true);
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const fetchPostById = useCallback(async (postId: number) => {
        try {
            const response = await axios.get(
                `https://veridian.adam-webdev.sk/wp-json/wp/v2/posts/${postId}?_embed`
            );
            setPost(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching post:', error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (post && post.translations) {
            const translatedPostId = post.translations[i18n.language as keyof typeof post.translations];
            if (translatedPostId) {
                fetchPostById(translatedPostId);
            }
        }
    }, [i18n.language, post, fetchPostById]);

    useEffect(() => {
        const fetchInitialPost = async () => {
            try {
                const response = await axios.get(
                    `https://veridian.adam-webdev.sk/wp-json/wp/v2/posts?slug=${slug}&_embed`
                );

                if (response.data && response.data.length > 0) {
                    const foundPost = response.data[0];
                    const isSkPost = foundPost.link.includes('/sk/');
                    
                    if ((i18n.language === 'sk' && isSkPost) || 
                        (i18n.language === 'en' && !isSkPost)) {
                        setPost(foundPost);
                    } else {
                        const translatedPostId = foundPost.translations[i18n.language as keyof typeof foundPost.translations];
                        if (translatedPostId) {
                            await fetchPostById(translatedPostId);
                        }
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching post:', error);
                setLoading(false);
            }
        };

        if (slug) {
            fetchInitialPost();
        }
    }, [slug, i18n.language, fetchPostById]);

    if (!slug) {
        return <div>{t('blog.noSlug')}</div>;
    }

    return (
        <div className={`blog-detail py-4 sm:py-10 ${theme}`}>
            <div className="container mx-auto px-4">
                <div className="flex justify-between items-center mb-8">
                    <button 
                        onClick={() => navigate('/blog')}
                        className="px-4 py-2 border rounded cursor-pointer"
                    >
                        ← {t('blog.backToBlog')}
                    </button>
                    
                </div>

                {loading ? (
                    <p className="text-center">{t('blog.loading')}</p>
                ) : post ? (
                    <article className="max-w-3xl mx-auto">
                        {post._embedded?.['wp:featuredmedia']?.[0]?.source_url && (
                            <img 
                                src={post._embedded['wp:featuredmedia'][0].source_url}
                                alt={post.title.rendered}
                                className="w-full h-64 object-cover rounded-lg mb-6"
                            />
                        )}
                        <h1 
                            className="text-4xl font-bold mb-6"
                            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                        />
                        
                        <div 
                            className="max-w-none blog-content"
                            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
                        />

                        {post.acf?.kratky_popis && (
                            <div className="text-xl italic text-gray-600 mb-6 font-medium">
                                {post.acf.kratky_popis}
                            </div>
                        )}
                        
                    </article>
                ) : (
                    <div className="text-center">
                        <p>{t('blog.postNotFound')}</p>
                        <button 
                            onClick={() => navigate('/blog')}
                            className="mt-4 px-4 py-2 border rounded hover:bg-gray-100"
                        >
                            {t('blog.returnToBlog')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}; 